import React, { FC } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Tooltip,
  Checkbox,
  FormGroup,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { FivePointScale } from "./FivePointScale";
import type { ImpactAnswers } from "./DMSurveyInternal";

interface ImpactAssessmentProps {
  currentAnswer: ImpactAnswers | undefined;
  onUpdate: (field: string, value: unknown) => void;
  bgColor: string;
  ranges: {
    likelihood: string[];
    intensity: string[];
    scope: string[];
    remediability: string[];
    timeHorizon: string[];
  };
}

export const ImpactAssessment: FC<ImpactAssessmentProps> = ({
  currentAnswer = {},
  onUpdate,
  bgColor,
  ranges,
}) => {
  const { t } = useTranslation();
  const showLikelihood = currentAnswer.isReal === false;
  const showIrremediability = currentAnswer.isPositiveImpact === false;

  return (
    <>
      {/* Actual/Potential */}
      <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="normal" sx={{ mb: 2 }}>
          {t(
            "dmSurvey.actualOrPotential",
            "Do you consider the impact actual or potential?",
          )}
          <Tooltip
            title={t(
              "dmSurvey.actualOrPotentialTooltip",
              "Actual impacts are already happening and require disclosure and remediation; potential impacts have not occurred yet but are plausible and must be monitored and prevented if they could become material.",
            )}
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: "text.secondary",
                ml: 1,
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </Typography>
        <RadioGroup
          row
          value={
            currentAnswer.isReal === true
              ? "actual"
              : currentAnswer.isReal === false
                ? "potential"
                : ""
          }
          onChange={(e) => onUpdate("isReal", e.target.value === "actual")}
        >
          <FormControlLabel
            value="actual"
            control={<Radio />}
            label={t("dmSurvey.actual", "Actual")}
          />
          <FormControlLabel
            value="potential"
            control={<Radio />}
            label={t("dmSurvey.potential", "Potential")}
          />
        </RadioGroup>
      </Box>

      {/* Positive/Negative */}
      <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="normal" sx={{ mb: 2 }}>
          {t(
            "dmSurvey.positiveOrNegative",
            "Do you consider the impact negative or positive?",
          )}
          <Tooltip
            title={t(
              "dmSurvey.positiveOrNegativeTooltip",
              "Positive impacts generate benefits for people or the environment, while negative impacts create harm that must be mitigated or compensated.",
            )}
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: "text.secondary",
                ml: 1,
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </Typography>
        <RadioGroup
          row
          value={
            currentAnswer.isPositiveImpact === true
              ? "positive"
              : currentAnswer.isPositiveImpact === false
                ? "negative"
                : ""
          }
          onChange={(e) =>
            onUpdate("isPositiveImpact", e.target.value === "positive")
          }
        >
          <FormControlLabel
            value="positive"
            control={<Radio />}
            label={t("dmSurvey.positive", "Positive")}
          />
          <FormControlLabel
            value="negative"
            control={<Radio />}
            label={t("dmSurvey.negative", "Negative")}
          />
        </RadioGroup>
      </Box>

      {/* Likelihood */}
      {showLikelihood && (
        <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="normal" gutterBottom>
            {t(
              "dmSurvey.assessLikelihood",
              "Assess the likelihood of the impact (that the impact materializes):",
            )}
            <Tooltip
              title={t(
                "dmSurvey.assessLikelihoodTooltip",
                "Estimate how probable the potential impact is considering current controls and context—higher likelihood increases impact materiality.",
              )}
            >
              <InfoOutlined
                sx={{
                  fontSize: 18,
                  color: "text.secondary",
                  ml: 1,
                  verticalAlign: "middle",
                }}
              />
            </Tooltip>
          </Typography>
          <FivePointScale
            value={currentAnswer.probability?.toString() || ""}
            onChange={(value) => onUpdate("probability", parseInt(value, 10))}
            labels={ranges.likelihood}
          />
        </Box>
      )}

      {/* Intensity */}
      <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="normal" sx={{ mb: 2 }}>
          {t(
            "dmSurvey.assessIntensity",
            "Assess the intensity/degree of impact (=severity/depth):",
          )}
          <Tooltip
            title={t(
              "dmSurvey.assessIntensityTooltip",
              "Estimate the severity or depth of the impact on the environment or people—higher intensity implies greater damage or benefit.",
            )}
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: "text.secondary",
                ml: 1,
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </Typography>
        <FivePointScale
          value={currentAnswer.intensity?.toString() || ""}
          onChange={(value) => onUpdate("intensity", parseInt(value, 10))}
          labels={ranges.intensity}
        />
      </Box>

      {/* Scope */}
      <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="normal" sx={{ mb: 2 }}>
          {t(
            "dmSurvey.assessScope",
            "Assess how widespread is the scope of the impact (e.g. the extent of environmental damage, number of affected individuals):",
          )}
          <Tooltip
            title={t(
              "dmSurvey.assessScopeTooltip",
              "Estimate the reach of the impact, such as the number of people affected or the geographical area involved.",
            )}
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: "text.secondary",
                ml: 1,
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </Typography>
        <FivePointScale
          value={currentAnswer.range?.toString() || ""}
          onChange={(value) => onUpdate("range", parseInt(value, 10))}
          labels={ranges.scope}
        />
      </Box>

      {/* Irremediable Character */}
      {showIrremediability && (
        <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
          <Typography variant="subtitle2" fontWeight="normal" sx={{ mb: 2 }}>
            {t(
              "dmSurvey.assessRemediability",
              "Assess the irremediable character of the impact (it is possible to restore the situation to its original state or to compensate for it):",
            )}
            <Tooltip
              title={t(
                "dmSurvey.assessRemediabilityTooltip",
                "Assess whether the negative impact can be restored to the original state or compensated—irremediable impacts are more material.",
              )}
            >
              <InfoOutlined
                sx={{
                  fontSize: 18,
                  color: "text.secondary",
                  ml: 1,
                  verticalAlign: "middle",
                }}
              />
            </Tooltip>
          </Typography>
          <FivePointScale
            value={currentAnswer.remediability?.toString() || ""}
            onChange={(value) => onUpdate("remediability", parseInt(value, 10))}
            labels={ranges.remediability}
          />
        </Box>
      )}

      {/* Time Horizon */}
      <Box sx={{ bgcolor: bgColor, p: 3, borderRadius: 1 }}>
        <Typography variant="subtitle2" fontWeight="normal" sx={{ mb: 2 }}>
          {t(
            "dmSurvey.assessTimeHorizon",
            "Assess the time horizon in which you expect the impact to occur:",
          )}
          <Tooltip
            title={t(
              "dmSurvey.assessTimeHorizonTooltip",
              "Select the timeframe(s) when the impact is expected to manifest.",
            )}
          >
            <InfoOutlined
              sx={{
                fontSize: 18,
                color: "text.secondary",
                ml: 1,
                verticalAlign: "middle",
              }}
            />
          </Tooltip>
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={currentAnswer.timeHorizon?.includes(0) || false}
                onChange={(e) => {
                  const current = currentAnswer.timeHorizon || [];

                  if (e.target.checked) {
                    onUpdate("timeHorizon", [...current, 0]);
                  } else {
                    onUpdate(
                      "timeHorizon",
                      current.filter((v) => v !== 0),
                    );
                  }
                }}
              />
            }
            label={
              <>{ranges.timeHorizon[0] || t("dmSurvey.shortTerm", "Short term")}</>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={currentAnswer.timeHorizon?.includes(1) || false}
                onChange={(e) => {
                  const current = currentAnswer.timeHorizon || [];

                  if (e.target.checked) {
                    onUpdate("timeHorizon", [...current, 1]);
                  } else {
                    onUpdate(
                      "timeHorizon",
                      current.filter((v) => v !== 1),
                    );
                  }
                }}
              />
            }
            label={
              <>{ranges.timeHorizon[1] || t("dmSurvey.mediumTerm", "Medium term")}</>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={currentAnswer.timeHorizon?.includes(2) || false}
                onChange={(e) => {
                  const current = currentAnswer.timeHorizon || [];

                  if (e.target.checked) {
                    onUpdate("timeHorizon", [...current, 2]);
                  } else {
                    onUpdate(
                      "timeHorizon",
                      current.filter((v) => v !== 2),
                    );
                  }
                }}
              />
            }
            label={
              <>{ranges.timeHorizon[2] || t("dmSurvey.longTerm", "Long term")}</>
            }
          />
        </FormGroup>
      </Box>
    </>
  );
};
